import { html, cx, useState, useMemo, useRef, useEffect } from '../h.js'
import { Icon } from '../icons.js'
import { t, plural } from '../i18n.js'
import { listTime, shortId, callLine } from '../format.js'
import { Avatar, Badges, Empty, StatusDot, useMenu, useToast, errorText } from '../components.js'
import { appVersion, compareVersions, fetchLatestVersion } from '../version.js'
import { useApp, useStore } from '../store.js'
import { ROOM_TYPE, GROUP_INVITE } from '@pine/core'
import { invitePreview } from './dialogs.js'
import { StoriesBar } from './stories.js'

const FILTERS = [
  { id: 'all', label: 'chats.all' },
  { id: 'unread', label: 'chats.unread' },
  { id: 'direct', label: 'chats.direct' },
  { id: 'groups', label: 'chats.groups' },
  { id: 'channels', label: 'chats.channels' },
  { id: 'archived', label: 'chats.archived' }
]

export function ChatList () {
  const store = useStore()
  const { client } = store
  const [query, setQuery] = useState('')
  const [filter, setFilter] = useState('all')
  const menu = useMenu()

  const rooms = client.listRooms()
  const requests = client.requests

  /*
   * The badges of everyone in a one-to-one chat, fetched for the whole list in
   * one request rather than one per row — see client.badgesForAll. The answer
   * is cached for minutes, so this costs nothing on a re-render and the list
   * simply draws them once they arrive.
   */
  const [badges, setBadges] = useState({})
  const peers = rooms.filter((r) => r.peer).map((r) => r.peer)
  const peerKey = peers.join(',')

  useEffect(() => {
    if (!peers.length) return
    let live = true
    client.badgesForAll(peers).then((map) => { if (live) setBadges(map) }).catch(() => {})
    return () => { live = false }
  }, [client, peerKey])

  const visible = useMemo(() => {
    const needle = query.trim().toLowerCase()
    return rooms.filter((room) => {
      if (filter === 'archived') { if (!room.archived) return false } else if (room.archived) return false
      if (filter === 'unread' && !room.unread) return false
      if (filter === 'direct' && room.type !== ROOM_TYPE.DM) return false
      if (filter === 'groups' && room.type !== ROOM_TYPE.GROUP) return false
      if (filter === 'channels' && room.type !== ROOM_TYPE.CHANNEL) return false
      if (needle) {
        const hay = (room.name || '') + ' ' + (room.lastPreview || '')
        if (!hay.toLowerCase().includes(needle)) return false
      }
      return true
    })
  }, [rooms, filter, query])

  return html`
    <aside class="pane">
      <header class="pane-head">
        <h1 class="grow">${t('chats.title')}</h1>
        <${StatusDot} status=${store.get('status')} />
        <button class="icon-btn" title=${t('new.title')}
                onClick=${() => store.showDialog({ type: 'new' })}>${Icon.plus(20)}</button>
      </header>

      <${UpdateBanner} store=${store} />
      <${NotifyBanner} store=${store} />
      ${client.selfFrozen && html`
        <div class="frozen-banner" role="status">
          <span class="frozen-mark">❄</span>
          <span><strong>${t('frozen.title')}</strong> ${t('frozen.body')}</span>
        </div>`}

      <div class="pane-search">
        <label class="search">
          ${Icon.search(17)}
          <input value=${query} placeholder=${t('chats.search')}
                 onInput=${(e) => setQuery(e.target.value)} />
          ${query && html`
            <button class="icon-btn" style="width:24px;height:24px"
                    onClick=${() => setQuery('')}>${Icon.close(14)}</button>`}
        </label>
      </div>

      <${FilterBar} value=${filter} onChange=${setFilter} />

      <div class="pane-body">
        ${!query && filter === 'all' && html`<${StoriesBar} />`}

        ${requests.length > 0 && html`
          <div>
            <div class="section-title">${t('contacts.requests')}</div>
            ${requests.map((req) => html`
              <${RequestRow} key=${req.invite} request=${req} />`)}
          </div>`}

        ${visible.length === 0
          ? html`<${Empty} icon=${Icon.chat(40)} title=${t('chats.empty')} message=${t('chats.emptyHint')}
                   action=${html`<button class="btn btn-primary" onClick=${() => store.showDialog({ type: 'new' })}>
                     ${Icon.plus(17)} ${t('new.title')}</button>`} />`
          : visible.map((room) => html`
              <${ChatRow} key=${room.id} room=${room} menu=${menu}
                badges=${room.peer ? badges[room.peer] : null} />`)}
      </div>
      ${menu.node}
    </aside>`
}

/**
 * The filter row scrolls sideways when the chips do not fit.
 *
 * A touch screen handles that natively, but a mouse has no horizontal wheel,
 * so on a desktop the chips past "Groups" were simply unreachable. A vertical
 * wheel over this row is translated into horizontal scroll; a thin scrollbar
 * appears on hover so the row does not look like it ends where it is clipped.
 */
function FilterBar ({ value, onChange }) {
  const ref = useRef(null)

  useEffect(() => {
    const node = ref.current
    if (!node) return

    const onWheel = (event) => {
      // Leave real horizontal intent (trackpads, shift+wheel) alone.
      if (Math.abs(event.deltaX) > Math.abs(event.deltaY)) return
      const max = node.scrollWidth - node.clientWidth
      if (max <= 0) return
      event.preventDefault()
      node.scrollLeft += event.deltaY
    }

    node.addEventListener('wheel', onWheel, { passive: false })
    return () => node.removeEventListener('wheel', onWheel)
  }, [])

  /** Keep a chip in view when it is picked from the keyboard. */
  function select (id, event) {
    onChange(id)
    const target = event.currentTarget
    if (target.scrollIntoView) {
      target.scrollIntoView({ inline: 'nearest', block: 'nearest', behavior: 'smooth' })
    }
  }

  return html`
    <div class="filters" ref=${ref} role="tablist">
      ${FILTERS.map((f) => html`
        <button key=${f.id} role="tab" aria-selected=${String(value === f.id)}
                class=${cx('filter', value === f.id && 'active')}
                onClick=${(e) => select(f.id, e)}>${t(f.label)}</button>`)}
    </div>`
}

function ChatRow ({ room, menu, badges = null }) {
  const store = useStore()
  const active = store.get('roomId') === room.id
  const isChannel = room.type === ROOM_TYPE.CHANNEL
  const isSaved = room.type === ROOM_TYPE.SAVED

  const name = isSaved ? t('chats.saved') : (room.name || shortId(room.peer || room.id))
  // One colour per person everywhere: a one-to-one chat takes the other
  // person's, as its header, the contacts and their messages do. It used to
  // take the room's, so the same face was orange here and pink inside.
  const face = room.type === ROOM_TYPE.DM && room.peer ? room.peer : room.id
  const assistant = store.client.assistant && room.peer && store.client.assistant.isAssistant(room.peer)
  // Nothing said yet: a quiet line rather than an empty one.
  const preview = room.lastCall
    ? '📞 ' + callLine(room.lastCall)
    : room.lastPreview || (assistant ? t('assistant.subtitle') : t('chats.noMessages'))

  // A chat the directory named but this device has no key for. There is
  // nothing to open until one of the members hands one over.
  if (room.placeholder) return html`<${RestoreRow} room=${room} name=${name} />`

  const contextItems = [
    {
      label: room.pinned ? t('panel.pinChat') + ' ✓' : t('panel.pinChat'),
      icon: Icon.pin(17),
      onSelect: () => store.client.setRoomFlags(room.id, { pinned: !room.pinned })
    },
    {
      label: room.muted ? t('panel.notifications') : t('panel.mute'),
      icon: room.muted ? Icon.bell(17) : Icon.bellOff(17),
      onSelect: () => store.client.setRoomFlags(room.id, { muted: !room.muted })
    },
    {
      label: t('panel.archive'),
      icon: Icon.archive(17),
      onSelect: () => store.client.setRoomFlags(room.id, { archived: !room.archived })
    },
    { separator: true },
    {
      label: t('panel.leave'),
      icon: Icon.logout(17),
      danger: true,
      onSelect: () => store.showDialog({ type: 'leave', roomId: room.id })
    }
  ]

  return html`
    <button class=${cx('chat-item', active && 'active')}
            onClick=${() => store.openRoom(room.id)}
            onContextMenu=${(e) => menu.open(e, contextItems)}>
      ${isSaved
        ? html`<div class="avatar" style="background:var(--pine-600)">${Icon.bookmark(20)}</div>`
        : html`<${Avatar} name=${name} id=${face} src=${room.avatar} square=${isChannel} />`}

      <div class="chat-main">
        <div class="chat-top">
          <span class="chat-name">
            ${isChannel && html`<span style="opacity:.55;margin-right:3px">#</span>`}${name}
          </span>
          ${Badges({ badges, size: 14 })}
          ${room.pinned && html`<span class="mute" style="flex:none">${Icon.pin(13)}</span>`}
          <span class="chat-time">${listTime(room.lastActivity)}</span>
        </div>
        <div class="chat-bottom">
          <span class=${cx('chat-preview', !room.lastCall && !room.lastPreview && 'quiet')}>${preview}</span>
          ${room.muted && html`<span class="mute" style="flex:none">${Icon.bellOff(14)}</span>`}
          ${room.unread > 0 && html`
            <span class=${cx('badge', room.muted && 'muted')}>${room.unread > 99 ? '99+' : room.unread}</span>`}
        </div>
      </div>
    </button>`
}

/**
 * The browser's notifications, offered once.
 *
 * In a browser nothing arrives while the tab is closed unless the person has
 * said yes to notifications — and the prompt can only be shown in answer to a
 * click. So a quiet line at the top of the chat list, until they answer or
 * wave it away; either way it is not shown again.
 */
function NotifyBanner ({ store }) {
  const notifications = store.platform.notifications
  const [hidden, setHidden] = useState(() => {
    try { return localStorage.getItem('pine:notify-banner') === 'done' } catch { return false }
  })
  const [state, setState] = useState(() => notifications ? notifications.state() : 'unsupported')

  if (!notifications || hidden || (state !== 'default' && state !== 'install')) return null

  const done = () => {
    try { localStorage.setItem('pine:notify-banner', 'done') } catch { /* private mode */ }
    setHidden(true)
  }

  // An iPhone in a Safari tab: nothing to allow here — it has to become an
  // app on the Home Screen first. Say how, once.
  if (state === 'install') {
    return html`
      <div class="notify-banner">
        <span class="glyph">${Icon.bell(17)}</span>
        <span class="grow">${t('web.notifyInstall')}</span>
        <button class="icon-btn" aria-label=${t('action.close')} onClick=${done}>${Icon.close(15)}</button>
      </div>`
  }

  return html`
    <div class="notify-banner">
      <span class="glyph">${Icon.bell(17)}</span>
      <span class="grow">${t('web.notifyBanner')}</span>
      <button class="btn btn-sm btn-primary" onClick=${async () => {
        const answer = await notifications.enable()
        setState(answer)
        done()
      }}>${t('access.allow')}</button>
      <button class="icon-btn" aria-label=${t('action.close')} onClick=${done}>${Icon.close(15)}</button>
    </div>`
}

/**
 * A chat that came back by name only.
 *
 * After restoring an account the server can say what the rooms were called and
 * who was in them, and no more than that — it has never held a key. Pressing
 * the button asks the people who are in it, and the first one online answers
 * with a way back in, history and all.
 */
function RestoreRow ({ room, name }) {
  const store = useStore()
  const toast = useToast()
  const [busy, setBusy] = useState(false)

  const asking = busy || store.client.isRestoring(room.id)
  const others = (room.members || []).filter((m) => m.account !== store.client.identity.id)

  async function restore () {
    setBusy(true)
    try {
      const { asked } = await store.client.restoreRoom(room.id)
      if (asked === 0) toast(t('restore.nobodyOnline'), { error: true })
      else toast(t('restore.asked', { n: asked }))
    } catch (err) {
      toast(err.message || t('err.generic'), { error: true })
    } finally {
      setBusy(false)
    }
  }

  return html`
    <div class="chat-item placeholder" style="cursor:default">
      <${Avatar} name=${name} id=${room.id} square=${room.type === ROOM_TYPE.CHANNEL} />
      <div class="chat-main">
        <div class="chat-top">
          <span class="chat-name">${name}</span>
        </div>
        <div class="chat-bottom">
          <span class="chat-preview">
            ${asking ? t('restore.asking') : plural('members', others.length + 1)}
          </span>
        </div>
      </div>
      <button class="btn btn-soft btn-sm" disabled=${asking} onClick=${restore}>
        ${asking ? html`<div class="spinner"></div>` : t('restore.action')}
      </button>
    </div>`
}

function RequestRow ({ request }) {
  const store = useApp()
  const toast = useToast()
  const [busy, setBusy] = useState(false)

  return html`
    <div class="chat-item" style="cursor:default">
      <${Avatar} name=${request.name} id=${request.from} />
      <div class="chat-main">
        <div class="chat-top"><span class="chat-name">${request.name || shortId(request.from)}</span></div>
        <div class="chat-bottom"><span class="chat-preview">${requestText(request)}</span></div>
      </div>
      <div class="row gap1">
        <button class="btn btn-primary btn-sm" disabled=${busy} onClick=${async () => {
          setBusy(true)
          try {
            await store.client.acceptRequest(request)
          } catch (err) {
            toast(errorText(err), { error: true })
          } finally {
            setBusy(false)
          }
        }}>${t('contacts.accept')}</button>
        <button class="icon-btn" onClick=${() => store.client.declineRequest(request)}>${Icon.close(16)}</button>
      </div>
    </div>`
}

/** "wants to start a chat", or which group the letter invites into. */
function requestText (request) {
  if (String(request.note || '').startsWith(GROUP_INVITE)) {
    const preview = invitePreview(request.invite)
    return t('contacts.inviteTo', { name: (preview && preview.name) || t('invite.unnamed') })
  }
  return t('contacts.request')
}

export function memberSummary (memberCount, onlineCount) {
  const parts = [plural('members', memberCount)]
  if (onlineCount > 0) parts.push(plural('online', onlineCount))
  return parts.join(', ')
}

/**
 * "A newer version is out."
 *
 * Pine cannot update itself — the Android build is a sideloaded APK and the
 * desktop is an installer — so the honest thing is to say a newer build exists
 * and link to where it lives. The server reports what the download page is
 * serving; anything older than that gets the banner.
 *
 * Closing it lasts for the session and no longer. A build that cannot update
 * itself has to keep asking: a dismissal remembered on disk meant one tap
 * silenced the notice for good, and phones stayed on old builds for weeks
 * while their owners reported bugs that were already fixed.
 */
function UpdateBanner ({ store }) {
  const [latest, setLatest] = useState(null)

  useEffect(() => {
    let live = true
    const mine = appVersion()
    fetchLatestVersion(store.client.registryUrl).then((found) => {
      if (!live || !found) return
      if (compareVersions(found.version, mine) <= 0) return
      setLatest(found)
    }).catch(() => {})
    return () => { live = false }
  }, [store])

  if (!latest) return null

  // Closed for this session only. A sideloaded APK does not update itself, so
  // the reminder is worth repeating at every launch until the new one is
  // actually installed — a version dismissed once used to stay silent forever.
  function dismiss () {
    setLatest(null)
  }

  return html`
    <div class="update-banner">
      <span class="glyph">${Icon.download(18)}</span>
      <span class="body">
        <span class="title">${t('update.title', { version: latest.version })}</span>
        <span class="sub">${t('update.sub')}</span>
      </span>
      <button class="btn btn-primary btn-sm" onClick=${() => {
        const platform = store.get('platform')
        if (platform && platform.openExternal) platform.openExternal(latest.downloads)
        else window.open(latest.downloads, '_blank', 'noopener')
      }}>${t('update.action')}</button>
      <button class="icon-btn" title=${t('action.cancel')} onClick=${dismiss}>${Icon.close(16)}</button>
    </div>`
}
